import React, { useState, useEffect, useRef, useCallback } from 'react';
import API_BASE_URL from "../Config.js";

// ==========================================
// 메인 통합 컴포넌트 (BoardApp)
// ==========================================
export default function BoardApp() {
  const [modalType, setModalType] = useState(null); // null, 'write', 'detail', 'reply'
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [boards, setBoards] = useState([]);
  const token = sessionStorage.getItem("token"); // 🔑 토큰 가져오기
  const currentUserId = sessionStorage.getItem("userId"); // 현재 로그인한 사용자 ID
  const currentUserGrant = sessionStorage.getItem("grantFg"); // 현재 로그인한 사용자 권한
  const [showModal, setShowModal] = useState(false);
  const [isReply, setIsReply] = useState(false);
  

 // 3. 백엔드 DB 데이터 가져오기 함수 (매핑 처리)
  const fetchBoards = async () => {
    try {
      let Search_id;

      if (currentUserGrant === "2") {
        Search_id = currentUserId; // 일반 사용자는 자신의 글만 조회
      } else {
        Search_id = ""; // 관리자는 모든 글 조회
      }

      const res = await fetch(`${API_BASE_URL}/boards`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' // 💡 정확하게 application/json 지정 및 중괄호 마감
          },
          body: JSON.stringify({ search_id: Search_id }) // 💡 Search_id를 JSON 형태로 전송 (백엔드에서 받는 형태에 맞게 조정)
      });

      if (!res.ok) throw new Error("서버 응답 오류: " + res.status);
      const data = await res.json();
      
      if (Array.isArray(data)) {
        setBoards(data); // 가져온 DB 데이터 배열을 State에 저장
        //console.log("게시글 데이터 로드 성공:", data);
      } else {
        console.error("잘못된 데이터 형식:", data);
      }
    } catch (err) {
      console.error("게시글 조회 실패:", err);
    }
  }; 

  const downloadFile = async (idx, seq, fileNm) => {
          try {
              const res = await fetch(`${API_BASE_URL}/files/download?idx=${idx}&seq=${seq}`, {
                  headers: { Authorization: `Bearer ${token}` }
              });
      
              //console.log(res)
              if (!res.ok) throw new Error("다운로드 실패");
  
              const blob = await res.blob();
              const url = window.URL.createObjectURL(blob);
  
              const a = document.createElement("a");
              a.href = url;
              a.download = fileNm; // 저장될 파일명
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
          } catch (err) {
              alert("다운로드 에러: " + err.message);
          }
      };

  const selectedBoard = boards.find(b => b.idx === selectedIdx);

  // 초기 데이터 로드
  useEffect(() => {
    fetchBoards();
  }, []);

  // 새 글 등록
  const handleSaveBoard = (newBoard) => {
    const nextIdx = boards.length > 0 ? Math.max(...boards.map(b => b.idx)) + 1 : 1;
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
    
    const completeBoard = {
      ...newBoard,
      idx: nextIdx,
      wr_date: now,
      in_date: now,
      reply_idx: null
    };

    setBoards([completeBoard, ...boards]);
    setModalType(null);
  };

  // 이다리 글쓰기
  const saveBoard = async (newBoard) => {
      if (!newBoard.project_name || !newBoard.director) {
          alert("과제명과 과제책임자명을 입력해 주세요!");
          return; // 저장 동작 중단
      }

      try {
          const res = await fetch(`${API_BASE_URL}/boards/new`, {
              method: "POST",
              headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                  ...newBoard,
                  files: undefined, // 게시글 등록 시에는 파일 제외
                  reply_idx: isReply ? selectedIdx : null,
                  state_fg: [3, 4].includes(Number(newBoard.state_fg)) ? Number(newBoard.state_fg) : 0
              })
          });
          //console.log("state_fg check:", newBoard.state_fg);
          
          const text = await res.text(); // 응답을 문자열로 받음
          let data = null;

          // 💡 1. JSON 파싱을 외부 변수(data)에 안전하게 담기
          try {
              data = JSON.parse(text);
          } catch {
              console.error("JSON 아님, 원문 응답:", text);
              alert("서버 오류: " + text); 
              return; // JSON이 아니면 게시글 생성 실패이므로 이후 파일 업로드 중단
          }

          // 💡 3. 파일 업로드 로직 실행 (data 변수가 정상 생성된 후 접근 가능)
          if (newBoard.files && newBoard.files.length > 0) {
              // 서버가 생성된 게시글의 새 idx를 주었는지 확인 필요 (보통 data.idx 또는 data.insertId 등으로 내려옵니다)
              const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    
              // 50MB를 초과하는 파일이 하나라도 있는지 확인
              const hasLargeFile = newBoard.files.some(file => file.size > MAX_FILE_SIZE);

              if (hasLargeFile) {
                  alert("첨부파일은 파일당 최대 50MB까지만 업로드할 수 있습니다.");
                  return; // 백엔드로 요청을 보내지 않고 중단
              }

              const boardIdx = data.idx; 

              if (!boardIdx) {
                  throw new Error("신청접수글 생성은 성공했으나, 저장된 게시글의 ID(idx)를 가져오지 못했습니다.");
              }

              // [수정] Promise.all을 제거하고 순서대로(직렬) 하나씩 요청을 보내고 대기합니다.
              for (const file of newBoard.files) {
                  const fd = new FormData();

                  fd.append("idx", boardIdx);
                  fd.append("fr_id", data.fr_id || newBoard.fr_id); 
                  fd.append("file", file); // 백엔드 multer/FormFile 등에서 받는 필드명

                  const fileRes = await fetch(`${API_BASE_URL}/files/upload`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${token}` },
                      body: fd
                  });

                  if (!fileRes.ok) {
                      throw new Error(`파일 업로드 실패: ${file.name}`);
                  }

                  // 한 파일의 응답이 완전히 끝날 때까지 대기(Await) 후 다음 파일 루프로 진입
                  await fileRes.json();
              }
          }

          alert("저장되었습니다!");

          setModalType(null); 
          
          if (typeof setIsReply === 'function') {
              setIsReply(false); // 댓글 관련 상태도 초기화
          }

          setShowModal(false);
          fetchBoards();   // 목록 갱신
      } catch(err) {
          console.error("전체 프로세스 에러:", err);
          alert("에러 발생: " + err.message);
      } 
    };  

  // 새글 쓰기 형태의 댓글 등록
  const handleSaveReply = (newReplyForm) => {
    const nextIdx = boards.length > 0 ? Math.max(...boards.map(b => b.idx)) + 1 : 1;
    const now = new Date().toISOString().replace('T', ' ').substring(0, 19);

    const completeReply = {
      ...newReplyForm,
      idx: nextIdx,
      wr_date: now,
      in_date: now,
      reply_idx: selectedIdx // 현재 선택되어 있던 원글의 idx를 부모로 지정
    };

    setBoards([...boards, completeReply]);
    setModalType(null);
  };

  return (
    <div>
      {/* [추가] 프로그램 제목 영역 */}
            <h2 style={{ marginBottom: "15px", color: "#333", borderBottom: "2px solid #333", paddingBottom: "10px" }}>
                📝 신청서 접수 목록 
            </h2>
      
      {/* 글 목록 (원글과 댓글이 정렬되어 함께 출력됨) */}
      <BoardList 
        boards={boards} 
        onWriteClick={() => setModalType('write')} 
        onDetailClick={(idx) => { setSelectedIdx(idx); setModalType('detail'); }} 
        fetchBoards={fetchBoards}
        currentUserId={currentUserId}
        currentUserGrant={currentUserGrant}
        token={token}
      />

      {/* 모달: 새글 쓰기 */}
      {modalType === 'write' && (
        <Modal onClose={() => {setIsReply(false);}}>
          <BoardForm 
            title="✍️ 신청서 작성"
            //onSave={handleSaveBoard} 
            onSave={saveBoard} 
            onCancel={() => setModalType(null)} 
            currentUserId={currentUserId}
            currentUserGrant={currentUserGrant}
            token={token}
            fetchBoards={fetchBoards} // <-- 필수 전달            
          />
        </Modal>
      )}

      {/* 모달: 상세보기 */}
      {modalType === 'detail' && (
        <Modal onClose={() => {}}>
          <BoardDetail 
            board={boards.find(b => b.idx === selectedIdx)} 
            onReplyClick={() => {setModalType('reply'); setIsReply(true);}} // 댓글 쓰기 모달로 전환
            onBack={() => setModalType(null)} 
            downloadFile={downloadFile}
            selectedBoard={selectedBoard}
            fetchBoards={fetchBoards} // <-- 필수 전달
            currentUserId={currentUserId} // 현재 로그인한 사용자 ID
            currentUserGrant={currentUserGrant} // 현재 로그인한 사용자 권한
            token={token}
            isReply={isReply}
          />
        </Modal>
      )}

      {/* 모달: 댓글 쓰기 (새글 쓰기와 동일한 폼 구조) */}
      {modalType === 'reply' && ( 
        <Modal onClose={() => {setIsReply(false);}}>
          <BoardForm 
            title="💬 Feedback 작성"            
            parentBoard={boards.find(b => b.idx === selectedIdx)} // 원글 정보 전달
            //onSave={handleSaveReply} 
            onSave={saveBoard} 
            onCancel={() => setModalType('detail')} // 취소 시 다시 상세보기로
            currentUserId={currentUserId}
            currentUserGrant={currentUserGrant}
            board={boards.find(b => b.idx === selectedIdx)} 
            onReplyClick={() => {setModalType('reply');}} // 댓글 쓰기 모달로 전환
            onBack={() => setModalType(null)} 
            downloadFile={downloadFile}
            selectedBoard={selectedBoard}
            fetchBoards={fetchBoards} // <-- 필수 전달
            token={token}
            isReply={isReply}
            modalType={modalType}
          />
        </Modal>
      )}
    </div>
  );
}

// ==========================================
// 공통 모달 프레임워크 컴포넌트 (BoardApp 외부 배치)
// ==========================================
function Modal({ children, onClose }) {
  return (
    /* 💡 최외곽 overlay 레이어에서 onClick={onClose}를 삭제했습니다. */
    <div style={styles.modalOverlay}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* 우측 상단 X 버튼은 그대로 두고 클릭 시 정상적으로 닫힙니다 */}
        <button onClick={onClose} style={styles.modalCloseBtn}></button>
        {children}
      </div>
    </div>
  );
}

// ==========================================
// 글 목록 컴포넌트
// ==========================================
function BoardList({ boards = [], onWriteClick, onDetailClick, fetchBoards, token, currentUserId, currentUserGrant }) {
  // --- [1. 검색 및 상태 필터 상태관리] ---
  const [searchType, setSearchType] = useState('fr_id'); // fr_id(글쓴이), subject(제목), contents(내용)
  const [searchKeyword, setSearchKeyword] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all(전체), 1(안읽음/요청), 2(읽음/진행), 3(완료) 등 DB 구조 매핑
  // 담당자 배정 모달 오픈 여부 및 입력 폼 상태
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ manager_id: '', sub_id: '' });

  // 사원(사용자) 목록을 담을 상태 추가
  const [users, setUsers] = useState([]);

  //const searchTerm = "";

  // 📌 [수정] 함수를 useCallback으로 감싸서 리렌더링 시 재생성되는 것을 막습니다.
  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/managers`, {
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}` 
        },
        method: 'POST',
        body: JSON.stringify({ searchTerm : "" })
      });
      if (!res.ok) throw new Error("사용자 목록 조회 실패: " + res.status);
      const data = await res.json();

      if (Array.isArray(data)) {
        setUsers(data);
      } else {
        console.error("잘못된 사용자 데이터 형식:", data);
      }
    } catch (err) {
      console.error("사용자 목록 가져오기 오류:", err);
    }
  }, [token]); // 📌 token이 바뀔 때만 함수가 새로 만들어짐 (비밀번호 변경 시에는 고정)

  // 모달이 열리거나 컴포넌트가 로드될 때 사원 목록 조회
  useEffect(() => {
    if (currentUserId) {
      fetchUsers();
    }
  }, [currentUserId, fetchUsers]); // 📌 이제 안전하게 넣어두어도 오작동하지 않습니다

  // 담당자 일괄 배정 저장 핸들러
  const handleAssignSave = async (e) => {
    e.preventDefault();
    if (selectedIdxs.length === 0) {
      alert("배정할 게시글을 선택해 주세요.");
      return;
    }
    if (!assignForm.manager_id) {
      alert("담당자(정)는 필수 입력 항목입니다.");
      return;
    }

    try {
      // 백엔드 일괄 업데이트 API 세팅 (BODY에 idx 배열과 배정 정보를 함께 전송)
      const res = await fetch(`${API_BASE_URL}/boards/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          idxs: selectedIdxs,             // 선택된 게시글 idx 배열 [1, 2, 3]
          manager_id: assignForm.manager_id,
          sub_id: assignForm.sub_id,
          assign_id: currentUserId,               // 배정 일시
          state_fg: 2                     // 배정 시 진행상태를 '진행중(2)'으로 변경
        })
      });
      
      //console.log("담당자 배정 send:", { idxs: selectedIdxs, manager_id: assignForm.manager_id, sub_id: assignForm.sub_id, assign_id: currentUserId, state_fg: 2 });
      if (!res.ok) throw new Error("서버 응답 오류: " + res.status);

      alert(`총 ${selectedIdxs.length}건의 게시글에 담당자 배정이 완료되었습니다.`);
      setIsAssignModalOpen(false);       // 모달 닫기
      setAssignForm({ manager_id: '', sub_id: '' }); // 폼 초기화
      setSelectedIdxs([]);               // 체크박스 선택 초기화

      if (fetchBoards) fetchBoards();    // 메인 리스트 새로고침
    } catch (err) {
      console.error("담당자 배정 실패:", err);
      alert("담당자 배정에 실패했습니다.");
    }
  };

  // --- [2. 페이징 상태관리] ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // 한 페이지에 보여줄 게시글 수

  // --- [3. 데이터 필터링 및 계층형 정렬 로직] ---
    // --- [데이터 필터링 및 계층형 정렬 로직 - 원글+댓글 통합 조건 검색] ---
  const getFilteredAndSortedBoards = () => {
    // 1단계: 개별 게시글이 검색/상태 조건에 부합하는지 판별하는 헬퍼 함수
    const isMatchCondition = (b) => {
      // 상태 필터링 조건 검사
      if (statusFilter !== 'all' && String(b.state_fg) !== statusFilter) {
        return false;
      }
      // 텍스트 검색 조건 검사 (글쓴이, 제목, 내용)
      if (searchKeyword.trim() !== '') {
        const targetValue = b[searchType] ? String(b[searchType]).toLowerCase() : '';
        if (!targetValue.includes(searchKeyword.toLowerCase())) {
          return false;
        }
      }
      return true;
    };

    // 2단계: 조건에 맞는 모든 글의 idx와 부모(reply_idx) id를 수집
    const matchedIdxs = new Set();       // 조건에 직접 맞는 글들의 idx
    const requiredParentIdxs = new Set(); // 조건에 맞은 댓글 때문에 무조건 띄워야 하는 원글의 idx

    boards.forEach(b => {
      if (isMatchCondition(b)) {
        matchedIdxs.add(b.idx);
        // 만약 조건에 맞은 글이 댓글이라면, 부모(원글) idx도 기록해둡니다.
        if (b.reply_idx) {
          requiredParentIdxs.add(b.reply_idx);
        }
      }
    });

    // 3단계: 노출할 원글(Main Boards) 선별
    // 조건에 직접 맞은 원글이거나, 혹은 조건에 맞은 댓글의 부모인 원글만 필터링합니다.
    const finalMainBoards = boards.filter(b => {
      if (b.reply_idx) return false; // 일단 원글만 골라냅니다.
      return matchedIdxs.has(b.idx) || requiredParentIdxs.has(b.idx);
    });

    // 4단계: 선별된 원글 최신순 정렬 및 결과 배열 생성
    const sortedMainBoards = finalMainBoards.sort((a, b) => b.idx - a.idx);
    const result = [];

    // 5단계: 정렬된 원글 아래에 해당 조건에 맞는 댓글들만 매핑하여 순서대로 삽입
    sortedMainBoards.forEach(main => {
      // 원글은 무조건 리스트에 추가
      result.push(main);

      // 해당 원글에 딸린 댓글 중에서 '실제 검색 조건에 부합하는 댓글'만 필터링
      const validReplies = boards.filter(b => {
        return b.reply_idx === main.idx && matchedIdxs.has(b.idx);
      }).sort((a, b) => new Date(a.wr_date) - new Date(b.wr_date)); // 댓글은 작성일 순 정렬

      result.push(...validReplies);
    });

    return result;
  };


  // 조건이 반영된 전체 리스트 계산
  const processedList = getFilteredAndSortedBoards();

  // --- [4. 페이징 데이터 계산] ---
  const totalItems = processedList.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  
  // 현재 페이지에 해당하는 데이터만 슬라이싱
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = processedList.slice(indexOfFirstItem, indexOfLastItem);

  // 검색 조건 변경 시 페이지를 1페이지로 리셋하기 위한 핸들러
  const handleFilterChange = (type, value) => {
    if (type === 'status') setStatusFilter(value);
    if (type === 'type') setSearchType(value);
    if (type === 'keyword') setSearchKeyword(value);
    setCurrentPage(1); // 조건 변경 시 첫 페이지로 이동
  };

    // --- [상태 관리를 위한 State 추가 필요] ---
  // 컴포넌트 상단에 아래 State가 선언되어 있어야 합니다.
  const [selectedIdxs, setSelectedIdxs] = useState([]);

  // 전체 선택/해제 핸들러
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // 현재 페이지에 보이는 모든 게시글의 idx를 배열에 담기
      const currentIdxs = currentItems.map(b => b.idx);
      setSelectedIdxs(currentIdxs);
    } else {
      setSelectedIdxs([]); // 전체 해제
    }
  };

  // 개별 선택/해제 핸들러
  const handleSelectIndividual = (idx, isChecked) => {
    if (isChecked) {
      setSelectedIdxs(prev => [...prev, idx]);
    } else {
      setSelectedIdxs(prev => prev.filter(id => id !== idx));
    }
  };

    return (
    <div>
      {/* ==================== 상담 이용 시 유의사항 안내 바 ==================== */}
      <div style={localStyles.noticeBox}>
        <div style={localStyles.noticeTitle}>
          <span style={{ marginRight: '6px' }}>💡</span>
          신청서 접수 시 유의 사항
        </div>
        <ul style={localStyles.noticeList}>
          <li>* 본 페이지는 세종대학교 산학협력단 신청/접수 화면입니다. </li>
          <li>* 과제의 신청, 협약 등 산학협력단 명의의 직인날인을 신청하는 양식입니다. 원활한 진행을 위해 필요 내용을 충실히 입력해 주시기 바랍니다.</li>
          <li>* 모든 과정은 대학의 지침에 따라 내부행정절차를 준수하여야 하는 바, 시일이 소요되므로 필요시기를 고려하시어 충분한 시간을 갖고 신청해 주시기 바랍니다.</li>
        </ul>
      </div>


      {/* ==================== 상단 검색 및 필터 바 ==================== */}
      <div style={localStyles.filterBar}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select 
            value={statusFilter} 
            onChange={(e) => handleFilterChange('status', e.target.value)}
            style={localStyles.select}
          >
            <option value="all">상태: 전체</option>
            <option value="0">신규</option>
            <option value="1">접수(읽음)</option>
            <option value="2">진행중(배정)</option>
            <option value="3">보완요청</option>
            <option value="4">완료</option>
          </select>

          <select 
            value={searchType} 
            onChange={(e) => handleFilterChange('type', e.target.value)}
            style={localStyles.select}
          >
            <option value="fr_name">요청자</option>
            <option value="project_name">과제명(번호)</option>
            <option value="director">과제책임자</option>
            <option value="manager_name">담당자(정)</option>
            <option value="wr_date">작성일</option>
            <option value="contents">요청사항</option>
          </select>

          <input 
            type="text" 
            placeholder="검색어를 입력하세요..." 
            value={searchKeyword}
            onChange={(e) => {
                setSearchKeyword(e.target.value);
                setCurrentPage(1);
            }}
            style={localStyles.searchInput}
          />

          <button 
              onClick={() => { 
                fetchBoards(); // 검색 조건에 맞는 게시글 재조회
              }} 
              style={{ ...styles.btnPrimary, background: '#e67e22' }}
            >
              조회
            </button>
        </div>

        {/* 기능 버튼 영역 */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {currentUserGrant !== "2" && (
            <button 
              onClick={() => { 
                if (selectedIdxs.length === 0) {
                  alert("담당자배정을 하려면 먼저 항목을 선택하세요.");
                } else {
                  setAssignForm({ manager_id: '', sub_id: '' }); 
                  setIsAssignModalOpen(true);
                }
              }} 
              style={{ ...styles.btnPrimary, background: '#007bff' }}
            >
              담당자배정
            </button>

          )}
          {currentUserGrant === "2" && (
            <button onClick={onWriteClick} style={styles.btnPrimary}>새 신청서 작성</button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px', color: '#666', marginBottom: '10px' }}>
        <div>
          검색 결과: 총 <b>{totalItems}</b>건 (페이지 {currentPage} / {totalPages})
        </div>
        {selectedIdxs.length > 0 && (
          <div style={{ color: '#e74c3c', fontWeight: 'bold' }}>
            선택된 항목: {selectedIdxs.length}건이 지정되었습니다.
          </div>
        )}
      </div>

      {/* ==================== 게시판 테이블 리스트 ==================== */}
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
        <thead>
          <tr style={{ background: '#f4f4f4', borderBottom: '2px solid #ccc' }}>
            <th style={{ ...styles.th, width: '40px', textAlign: 'center' }}>
              <input 
                type="checkbox" 
                onChange={handleSelectAll}
                checked={currentItems.length > 0 && currentItems.every(b => selectedIdxs.includes(b.idx))}
                style={{ cursor: 'pointer' }}
              />
            </th>
            <th style={styles.th}>번호</th>
            <th style={styles.th}>진행상태</th>
            <th style={styles.th}>과제번호(과제명)</th>
            <th style={styles.th}>요청자</th>
            <th style={styles.th}>과제책임자</th>
            <th style={styles.th}>시행기한</th>
            <th style={styles.th}>담당자(정)</th>
            <th style={styles.th}>담당자(부)</th>
            {/*  나중에 요청하면 열어준다(배정한 마스터계정)
             {currentUserGrant !== "2" && (
              <th style={styles.th}>배정담당</th>
             )}
            */}
            <th style={styles.th}>작성일</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.length === 0 ? (
            <tr><td colSpan="9" style={{ ...styles.td, textAlign: 'center', color: '#999', padding: '30px' }}>조회된 게시글이 없습니다.</td></tr>
          ) : (
            currentItems.map(b => (
              <tr 
                key={b.idx} 
                style={{ 
                  borderBottom: '1px solid #ddd', 
                  background: selectedIdxs.includes(b.idx) ? '#ebf5fb' : (!!b.reply_idx ? '#fdfdfd' : '#fff'),
                  transition: 'background 0.15s'
                }}
              >
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <input 
                    type="checkbox" 
                    checked={selectedIdxs.includes(b.idx)}
                    onChange={(e) => handleSelectIndividual(b.idx, e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                </td>
                <td style={styles.td}>{b.idx}</td>
                <td style={styles.td}>
                  {b.state_fg === 0 ? '신규' : b.state_fg === 1 ? '접수' : b.state_fg === 2 ? '진행중' : b.state_fg === 3 ? '보완요청' : b.state_fg === 4 ? '완료' : 'Unknown'}
                </td>
                <td style={styles.td}>  
                  <span style={{ 
                    paddingLeft: b.reply_idx ? '20px' : '0px', 
                    color: b.reply_idx ? '#e67e22' : 'inherit' 
                  }}>
                    {!!b.reply_idx && <span style={{ marginRight: '5px', fontWeight: 'bold' }}>└ Re:</span>}
                    <span onClick={() => onDetailClick(b.idx)} style={{ color: '#0066cc', cursor: 'pointer', fontWeight: b.reply_idx ? 'normal' : 'bold' }}>
                      {b.project_name || '-'}
                    </span>
                  </span>
                </td>
                <td style={styles.td}>{b.fr_name || '-'}</td>
                <td style={styles.td}>{b.director || '-'}</td>
                <td style={styles.td}>{b.finish_date || '-'}</td>
                <td style={styles.td}>{b.manager_name || '-'}</td>
                <td style={styles.td}>{b.sub_name || '-'}</td>
                 {/* 나중에 요청하면 열어준다(배정한 마스터계정)
                 {currentUserGrant !== "2" && (
                    <td style={styles.td}>{b.assign_name || '-'}</td>
                 )}
                */}
                <td style={styles.td}>{b.wr_date?.substring(0, 10) || '-'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* ==================== 하단 페이징 컨트롤 바 ==================== */}
      {totalItems > 0 && (
        <div style={localStyles.paginationBar}>
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{ ...localStyles.pageBtn, opacity: currentPage === 1 ? 0.5 : 1 }}
          >
            이전
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              style={{
                ...localStyles.pageBtn,
                background: currentPage === page ? '#333' : '#fff',
                color: currentPage === page ? '#fff' : '#333',
                fontWeight: currentPage === page ? 'bold' : 'normal'
              }}
            >
              {page}
            </button>
          ))}

          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={{ ...localStyles.pageBtn, opacity: currentPage === totalPages ? 0.5 : 1 }}
          >
            다음
          </button>
        </div>
      )}

      {/* ==================== [신규 추가] 담당자 배정 모달 레이어 ==================== */}
      {isAssignModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setIsAssignModalOpen(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, borderBottom: '1px solid #eee', paddingBottom: '10px' }}>💼 게시글 담당자 배정</h3>
            <p style={{ fontSize: '13px', color: '#666' }}>선택한 <b>{selectedIdxs.length}개</b>의 업무에 담당자를 일괄 배정합니다.</p>
            
            <form onSubmit={handleAssignSave} style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '15px' }}>
              {/* 담당자 (정) 콤보박스 */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', marginBottom: '5px', fontWeight: 'bold' }}>담당자 (정) *</label>
                <select 
                  value={assignForm.manager_id}
                  onChange={(e) => setAssignForm({ ...assignForm, manager_id: e.target.value })}
                  style={{ ...styles.input, height: '38px', padding: '6px 12px' }}
                  required 
                >
                  <option value="">-- 담당자(정) 선택 --</option>
                  {users.map(u => (
                    // DB의 사원 ID 필드가 user_id, 이름 필드가 user_nm 인 경우 기준 예시입니다.
                    <option key={u.login_id} value={u.login_id}>
                      {u.name} ({u.login_id})
                    </option>
                  ))}
                </select>
              </div>
                
              {/* 담당자 (부) 콤보박스 */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', marginBottom: '5px', fontWeight: 'bold' }}>담당자 (부)</label>
                <select 
                  value={assignForm.sub_id}
                  onChange={(e) => setAssignForm({ ...assignForm, sub_id: e.target.value })}
                  style={{ ...styles.input, height: '38px', padding: '6px 12px' }}
                >
                  <option value="">-- 선택 안함 --</option>
                  {users.map(u => (
                    <option key={u.login_id} value={u.login_id}>
                      {u.name} ({u.login_id})
                    </option>
                  ))}
                </select>
              </div>
                
              {/* 하단 제어 버튼 영역 */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '10px' }}>
                <button 
                  type="button" 
                  onClick={() => setIsAssignModalOpen(false)} 
                  style={{ ...styles.btnPrimary, background: '#aaa' }}
                >
                  취소
                </button>
                <button 
                  type="submit" 
                  style={{ ...styles.btnPrimary, background: '#2ecc71' }}
                >
                  배정 저장
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );


}

// 컴포넌트 전용 추가 컴포넌트 인라인 스타일 정의
const localStyles = {
  filterBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f9f9f9',
    padding: '12px',
    borderRadius: '6px',
    marginBottom: '15px',
    border: '1px solid #e5e5e5',
    gap: '10px'
  },
  select: {
    padding: '6px 10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    outline: 'none',
    fontSize: '14px'
  },
  searchInput: {
    padding: '6px 12px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    width: '200px',
    outline: 'none',
    fontSize: '14px'
  },
  paginationBar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5px',
    marginTop: '20px'
  },
  pageBtn: {
    padding: '5px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    background: '#fff',
    cursor: 'pointer',
    fontSize: '13px',
    transition: 'all 0.2s'
  },
  noticeBox: {
    backgroundColor: '#fffdf9',       // 아주 연한 주황/베이지빛 배경으로 시선 집중
    border: '1px solid #fbe5d6',      // 부드러운 주황빛 테두리
    borderRadius: '8px',              // 모서리 라운딩 처리
    padding: '16px 20px',             // 여백 확보
    marginTop: '15px',                // 검색바와의 간격
    marginBottom: '20px',             // 아래 보드 리스트와의 간격
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)', // 은은한 그림자 효과
  },
  noticeTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#d35400',                 // 강조를 위한 짙은 주황색 치우침
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
  },
  noticeList: {
    margin: '0',
    paddingLeft: '20px',              // 리스트 점(bullet) 영역 확보
    fontSize: '13px',
    color: '#555555',                 // 눈이 편안한 다크그레이 색상
    lineHeight: '1.6',                // 줄간격 자간 확보로 가독성 향상
  }


};


// ==========================================
// 작성 폼 컴포넌트 (새글/댓글 공용 양식)
// ==========================================
function BoardForm({ title, parentBoard, onSave, onCancel, currentUserId, currentUserGrant, token, fetchBoards, isReply, modalType }) {
  // 댓글 모달로 열린 경우 원글 정보를 바탕으로 초기 폼 값을 세팅해줍니다.
  const [form, setForm] = useState({
    fr_id: currentUserId || '', // 작성자 ID는 현재 로그인한 사용자로 기본 세팅
    fr_name: currentUserId || '', // 작성자 ID는 현재 로그인한 사용자로 기본 세팅
    to_id: parentBoard ? parentBoard.fr_id : '', // 댓글이면 원글 작성자가 수신자
    subject: parentBoard ? `[RE] ${parentBoard.subject}` : '', // 댓글 기본 제목 세팅
    project_name: parentBoard ? `[RE] ${parentBoard.project_name}` : '', // 댓글 기본 제목 세팅
    contents: '',
    state_fg: 0, //parentBoard ? parentBoard.state_fg : 1,
    manager_id: parentBoard ? parentBoard.manager_id || '' : '',
    manager_name: parentBoard ? parentBoard.manager_name || '' : '',
    sub_id: parentBoard ? parentBoard.sub_id || '' : '',
    sub_name: parentBoard ? parentBoard.sub_name || '' : '',
    files: [],
    assign_id: parentBoard ? parentBoard.assign_id || '' : '',
    assign_name: parentBoard ? parentBoard.assign_name || '' : '',

    req_no: parentBoard ? parentBoard.req_no || 0 : 0,
    tel: parentBoard ? parentBoard.tel || '' : '',
    email: parentBoard ? parentBoard.email || '' : '',
    director: parentBoard ? parentBoard.director || '' : '',
    major: parentBoard ? parentBoard.major || '' : '',
    support_comp: parentBoard ? parentBoard.support_comp || '' : '',
    support_name: parentBoard ? parentBoard.support_name || '' : '',
    support_tel: parentBoard ? parentBoard.support_tel || '' : '',
    finish_date: parentBoard ? parentBoard.finish_date || '' : ''
  });

  const fileInputRef = useRef(null);   

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: name === 'state_fg' ? Number(value) : value });
    setForm({ ...form, [name]: name === 'req_no' ? Number(value) : value  });
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1); // 오늘에 1일을 더함
    const year = tomorrow.getFullYear();
    const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const day = String(tomorrow.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    //console.log("댓글구분 상태:", currentUserGrant, form.state_fg);
    if (!form.project_name || !form.director || !form.fr_id) {
      alert('작성자(fr_id), 과제명, 과제책임자는 필수 입력 항목입니다.');
      //console.log("작성자(fr_id), 과제명, 과제책임자는 필수 입력 항목입니다. : ", form.project_name  ,form.director ,form.fr_id, form.state_fg);
      if (currentUserGrant !== 2) {
        
        if (form.state_fg === 0 || !form.state_fg ) {
          alert('Feeadback구분은 필수 입력 항목입니다.');
        }
      }
      return;
    }
    onSave(form);
  };
  // 1번 방법 추가 및 수정할 스타일
const styles = {
  row: {
    display: 'flex',
    alignItems: 'flex-start', // 상단 기준 정렬
    gap: '16px',
    width: '100%',
    marginBottom: '6px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',   // 라벨과 인풋을 세로로 배치 (핵심!)
    gap: '4px',                // 라벨과 인풋 사이 간격
    flex: '1',                 // 세 영역이 가로폭을 균등하게 분할
    minWidth: '0',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#666a71',          // 조금 더 또렷한 다크 그레이
  },
  input: {
    width: '100%',             // 부모 fieldGroup 너비에 맞춤
    padding: '10px 12px',
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    fontSize: '14px',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',             // 가로폭을 부모 상자 크기에 100% 맞춤
    minHeight: '50px',        // 최소 세로 높이 지정 (원하는 만큼 숫자를 올리세요)
    padding: '12px',           // 내부 여백을 살짝 더 여유롭게 설정
    border: '1px solid #E2E8F0',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',     // 글꼴이 기본 폰트로 바뀌는 것 방지
    lineHeight: '1.5',         // 줄간격을 넓혀 글을 읽기 편하게 설정
    boxSizing: 'border-box',
    
    // 🛠️ 마우스 드래그 크기 조절 옵션 (택 1)
    resize: 'vertical',     // 위아래(세로)로만 크기를 늘릴 수 있게 허용
  },
  // 분류 선택 전체 행 (위치가 제목 오른쪽 옆에 나란히 오도록 배치)
  formRowSingle: {
    display: 'flex',
    alignItems: 'flex-start', // 2줄 레이아웃이므로 제목이 첫 줄 상단에 정렬되도록 고정
    width: '100%',
    padding: '4px 0',
  },

  // 개별 라디오 항목 내부 스타일 (글씨 크기 및 정렬 통일)
  compactRadioLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '12px',
    cursor: 'pointer',
    color: '#333',
    minWidth: '70px', // 항목별 시작선 위치를 균등하게 고정하는 장치
  },

  cancelBtn: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#ffffff',
    backgroundColor: '#94A3B8', // 세련된 쿨그레이 색상
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },

  // 작성완료 버튼 공통 스타일
  submitBtn: {
    padding: '10px 24px',       // 조금 더 넓게 설정해서 안정감 부여
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  }
};

  return (
  <div style={{ 
    maxHeight: '100vh',       /* 화면 높이의 100%까지만 커지도록 제한 */
    overflowY: 'auto',       /* 내용이 넘치면 폼 전체에 세로 스크롤바 생성 */
    paddingRight: '12px',    /* 스크롤바가 콘텐츠를 가리지 않도록 우측 여백 확보 */
    boxSizing: 'border-box'
  }}>
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      <h3 style={{ marginTop: 0, color: parentBoard ? '#e67e22' : '#333' }}>{title}</h3>
      {parentBoard && (
        <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', fontSize: '12px', marginBottom: '10px', borderLeft: '3px solid #e67e22' }}>
          <b>원글 정보:</b> {parentBoard.project_name} (작성자: {parentBoard.fr_id}) {isReply && <span style={{ color: '#e67e22', fontWeight: 'bold', marginLeft: '10px' }}>(댓글 작성 중)</span>}
        </div>
      )}
      <div style={styles.row}>
        <input type="hidden" name="fr_id" value={form.fr_id} placeholder="보내는 사람 (fr_id) *" onChange={handleChange} style={styles.input} required />
        <input type="hidden" name="to_id" value={form.to_id} placeholder="받는 사람 (to_id)" onChange={handleChange} style={styles.input} />
      </div>

      { modalType !=='reply' &&(
        <>
      {/* 5. 분류 선택 (라디오 버튼) */}
      <div style={styles.formRowSingle}>
        {/* 1. 왼쪽 고정 폭 제목 */}
        <label style={styles.label}>🔸 요청항목</label>

        {/* 2. 오른쪽 라디오 버튼 감싸는 메인 박스 (세로 배열) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, marginLeft: '15px', paddingLeft: '15px',  
                      width: '100%', padding: '10px 12px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px',
                      boxSizing: 'border-box'}}>
                      
          {/* 윗줄: 항목A, 항목B, 항목C */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
            {['산학협력단 직인 날인', '외부공문발송', '과제신청'].map((item, idx) => (
              <label key={idx} style={styles.compactRadioLabel}>
                <input 
                  type="radio" 
                  name="req_no" 
                  value={idx + 1} 
                  checked={(form.req_no || 1) === idx + 1}
                  onChange={handleChange}
                  style={styles.radioInput}
                />
                {item}
              </label>
            ))}
          </div>
          
          {/* 아랫줄: 항목D, 항목E */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
            {['협약진행', '과제입찰참가요청'].map((item, idx) => {
              const realValue = idx + 4; // 항목D는 4, 항목E는 5
              return (
                <label key={realValue} style={styles.compactRadioLabel}>
                  <input 
                    type="radio" 
                    name="req_no" 
                    value={realValue} 
                    checked={(form.req_no || 1) === realValue}
                    onChange={handleChange}
                    style={styles.radioInput}
                  />
                  {item}
                </label>
              );
            })}
          </div>
          
        </div>
      </div>
      </>
      )}
      
      <div style={styles.row}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸과제번호(과제명) *</label>
          <input type="text" name="project_name" value={form.project_name} placeholder="기존과제는 과제번호를, 과제번호가 없는 경우 간단한 식별 단어를 넣어주세요." onChange={handleChange} style={styles.input} required />
        </div>
      </div>
      { modalType !=='reply' &&(
        <>
      <div style={styles.row}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸요청자 (로그인ID) *</label>
          <input type="text" name="fr_id" value={form.fr_id} placeholder="요청자" style={styles.input} readonly disabled required />
        </div>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸연락처 *</label>
          <input type="text" name="tel" value={form.tel} placeholder="010-0000-0000" onChange={handleChange} style={styles.input} required />
        </div>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸이메일</label>
          <input type="text" name="email" value={form.email} placeholder="이메일" onChange={handleChange} style={styles.input}  />        
        </div>
      </div>

      <div style={styles.row}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸과제책임자명 *</label>
          <input type="text" name="director" value={form.director} placeholder="과제책임자명" onChange={handleChange} style={styles.input} required />
        </div>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸과제책임자 학과명 *</label>
          <input type="text" name="major" value={form.major} placeholder="동명이인 확인용" onChange={handleChange} style={styles.input} required/>
        </div>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸진행기한 *</label>
          <input 
            type={form.finish_date ? "date" : "text"} /* 값이 있으면 date, 없으면 text */
            name="finish_date"             
            value={form.finish_date} 
            onChange={handleChange} 
            placeholder="당일진행은 불가할 수 있습니다." /* 이제 placeholder가 정상 작동합니다! */
            onFocus={(e) => (e.target.type = "date")} /* 클릭하면 달력으로 변환 */
            onBlur={(e) => { if (!e.target.value) e.target.type = "text"; }} /* 입력값 없으면 다시 text로 */
            style={styles.input} 
            min={getTomorrowDate()}
            required 
          />       
        </div>
      </div>

      <div style={styles.row}>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸과제지원기관</label>
          <input type="text" name="support_comp" value={form.support_comp} placeholder="과제지원기관" onChange={handleChange} style={styles.input}  />
        </div>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸과제지원기관 담당자</label>
          <input type="text" name="support_name" value={form.support_name} placeholder="(필요시)협약진행용 연락처" onChange={handleChange} style={styles.input} />
        </div>
        <div style={styles.fieldGroup}>
          <label style={styles.label}>🔸과제지원기관 담당자연락처</label>
          <input type="text" name="support_tel" value={form.support_tel} placeholder="(필요시)협약진행용 연락처" onChange={handleChange} style={styles.input}  />        
        </div>
      </div>
      </>
      )}

      <textarea 
        name="contents" 
        placeholder="요청사항을 구체적으로 입력해 주셔야 진행이 가능합니다. *" 
        rows="2"  
        required
        onChange={handleChange} 
        style={styles.textarea}    
        value={form.contents || ""} // 🌟 닫는 태그 안의 텍스트를 value 속성으로 이동
      />
 
      <div style={styles.row}>
        <input type="hidden" name="manager_id" value={form.manager_id} placeholder="관리자 ID" onChange={handleChange} style={styles.input} />
        <input type="hidden" name="sub_id" value={form.sub_id} placeholder="서브 ID" onChange={handleChange} style={styles.input} />
        <input type="hidden" name="assign_id" value={form.assign_id} placeholder="배정자 ID" onChange={handleChange} style={styles.input} />
      </div>

      {/* 댓글 작성 시에만 상태 변경 옵션 노출, 그리고 교수, 학생은 상태 변경 옵션을 보지 못하도록 조건 추가 */}
      {parentBoard && currentUserGrant !== "2" && (
        <div >
          <label style={{ 
            marginRight: '12px', 
            fontSize: '14px', 
            fontWeight: 'bold', 
            color: '#575449', 
            //background: '#575449', 
            padding: '4px 0px',
          }}>Feeadback 구분(필수 선택) : </label>
          <select name="state_fg" value={form.state_fg} onChange={handleChange} style={{ padding: '5px' }} required>
            <option value="">-- 구분 선택 --</option>
            <option value="3">보완요청</option>
            <option value="4">완료</option>          
          </select>

          {/* 💡 아랫쪽에 강제로 한 줄 공간을 만들어주는 태그 */}
          <div style={{ height: '24px' }}></div> 
        </div>
      )}



      {/* 파일 선택 버튼 */}
      <div className="file-upload-header">
          <input
              type="file"
              multiple
              onChange={e => {
                  const selectedFiles = Array.from(e.target.files);
                  setForm(prev => ({
                      ...prev,
                      files: [...prev.files, ...selectedFiles]
                  }));
              }}
              ref={fileInputRef}
          />
          <span className="upload-hint">파일 선택 버튼을 누르거나 파일을 드래그 앤 드롭하세요.</span>
      </div>
      
      <div
          className="drop-zone"
          onDragOver={e => e.preventDefault()}
          onDrop={e => {
              e.preventDefault();
              const droppedFiles = Array.from(e.dataTransfer.files);
              setForm(prev => ({
                  ...prev,
                  files: [...prev.files, ...droppedFiles]
              }));
          }}
      >
          {/* 선택된 파일 버튼 리스트 */}
          <div className="file-buttons">
              {form.files.map((file, index) => (
                  <button
                      key={index}
                      draggable
                      onDragEnd={e => {
                          // 드래그가 drop-zone 밖에서 끝나면 삭제
                          const dropZone = e.currentTarget.closest(".drop-zone");
                          const rect = dropZone.getBoundingClientRect();
      
                          if (
                              e.clientX < rect.left ||
                              e.clientX > rect.right ||
                              e.clientY < rect.top ||
                              e.clientY > rect.bottom
                          ) {
                              setForm(prev => ({
                                  ...prev,
                                  files: prev.files.filter((_, i) => i !== index)
                              }));
                          }
                      }}
                  >
                  {file.name}
                  </button>
              ))}
          </div>
      </div>
      

      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
        <button type="button" onClick={onCancel} style={styles.cancelBtn}>취소</button>
        <button type="submit" style={{ 
            ...styles.submitBtn, 
            backgroundColor: parentBoard ? '#e67e22' : '#4CAF50' // 조건부 색상만 인라인으로 유지
          }}>작성완료</button>
      </div>

    </form>
  </div>
  );
}

// ==========================================
// 상세보기 컴포넌트
// ==========================================
function BoardDetail({ board, onReplyClick, onBack, downloadFile, selectedBoard, fetchBoards, token, currentUserId, currentUserGrant }) {
  
  // --- [상세 모달이 열릴 때 chk_date 업데이트 트리거] ---
  useEffect(() => {
    // 이미 확인 날짜가 기록되어 있다면 중복 업데이트 방지를 위해 리턴 가능
    // 만약 매번 열 때마다 갱신해야 한다면 조건문(!board.chk_date)을 제거하세요.
    if (board && !board.chk_date && currentUserGrant !== "2") { // 읽음 처리 권한이 없는 사용자는 체크하지 않도록 조건 추가
      updateCheckDate(board.idx);
    }
  }, [board?.idx]);

  // --- [DB chk_date 및 chk_id 업데이트 API 호출 함수] ---
  const updateCheckDate = async (boardIdx) => {
    try {
      //const currentUserId = sessionStorage.getItem("userId"); // 현재 로그인한 사용자 ID
      // 백엔드 API 설계에 맞게 URL 및 Body를 맞춰주세요. (PUT 또는 PATCH 권장)
      const res = await fetch(`${API_BASE_URL}/boards/state`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          chk_id: currentUserId, // 확인한 사용자 ID
          idx: boardIdx, // 확인한 게시물 ID
          state_fg: board.state_fg === 3 || board.state_fg === 4 ? board.state_fg : 1 // 예시로 '읽음' 상태로 업데이트 (DB 구조에 맞게 조정 필요)
        })
      });
      //console.log("chk_date 업데이트 API 응답:", currentUserId, boardIdx, res);

      if (!res.ok) throw new Error("서버 응답 오류: " + res.status);
      
      // DB 업데이트 성공 후, 메인 화면 리스트 데이터를 실시간 갱신(새로고침)합니다.
      if (fetchBoards) {
        fetchBoards();
      }
    } catch (err) {
      console.error("확인 시간(chk_date) 업데이트 실패:", err);
    }
  };

  // 🛠️ 가독성을 위한 컴팩트 카드 전용 스타일
const detailBoxStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  backgroundColor: '#ffffff',         // 각 정보 필드마다 순백색 미니 박스 적용 (핵심!)
  padding: '10px 14px',
  borderRadius: '6px',
  border: '1px solid #EDF2F7',        // 은은한 경계선으로 정보를 독립적으로 분리
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.01)'
};

const detailLabelStyle = {
  fontSize: '11px',
  color: '#718096',                   // 라벨은 한눈에 인지되도록 연한 회색 톤 유지
  fontWeight: '600',
  letterSpacing: '-0.2px'
};

const detailValueStyle = {
  fontSize: '14px',
  color: '#1A202C',                   // 실데이터는 진하고 또렷한 챠콜 블랙
  fontWeight: '500',
  wordBreak: 'break-all'              // 긴 텍스트가 들어와도 영역 밖으로 깨지지 않게 방어
};

// 기존 상세 페이지용 스타일에 추가할 변수들
const radioLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  fontSize: '12px',
  color: '#334155', // 기존 Slate 테마와 잘 어울리는 어두운 회색
  cursor: 'default',
  minWidth: '70px',
};

const radioInputStyle = {
  accentColor: '#e67e22', // 선택된 항목의 주황색 테마 유지
  cursor: 'default',
};



  return (
  <div style={{ 
    maxHeight: '100vh',       /* 화면 높이의 100%까지만 커지도록 제한 */
    overflowY: 'auto',       /* 내용이 넘치면 폼 전체에 세로 스크롤바 생성 */
    paddingRight: '12px',    /* 스크롤바가 콘텐츠를 가리지 않도록 우측 여백 확보 */
    boxSizing: 'border-box'
  }}>
    {/* 1. 상단 정보 영역 (기존 스타일 100% 그대로 유지) */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: '12px', color: '#888' }}>
        번호: {board.idx} {!!board.reply_idx ? `(댓글원글번호: ${board.reply_idx})` : ''}
      </span>
      {/* 확인 날짜 표시 가시화 영역 추가 */}
      {board.chk_date && (
        <span style={{ fontSize: '11px', color: '#27ae60', background: '#e8f8f5', padding: '2px 6px', borderRadius: '4px' }}>
          👁️ 읽음 ({board.chk_date.substring(0, 16)})
        </span>
      )}
    </div>

    <h3 style={{ margin: '5px 0 12px 0', color: !!board.reply_idx ? '#e67e22' : '#333' }}>
      {!!board.reply_idx && '└ Re: '} {board.project_name}
    </h3>
    
    <div style={{ fontSize: '13px', color: '#666', display: 'flex', gap: '15px' }}>
      <span>작성: <b>{board.fr_id}</b></span>
      {/*<span>수신: <b>{board.to_id || '-'}</b></span> */}
      <span>담당(정): <b>{board.manager_name || '미지정'}</b></span>
      <span>담당(부): <b>{board.sub_name || '미지정'}</b></span>
      <span>날짜: {board.wr_date}</span>
    </div>

    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(3, 1fr)', 
      gap: '12px 16px', 
      backgroundColor: '#F8FAFC',                // 훨씬 투명하고 맑은 Slate 연회색 배경
      padding: '18px', 
      borderRadius: '12px',                      // 라운딩을 조금 더 부드럽게 확장
      border: '1px solid #E2E8F0',
      marginTop: '20px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)' // 아주 부드러운 하단 그림자 효과
    }}>
    {/* 🌟 [행 4] 분류 선택 그룹 (기존 스타일 및 구조 계승) */}
    <div style={{ ...detailBoxStyle, gridColumn: 'span 3', flexDirection: 'row', alignItems: 'center', gap: '20px', paddingTop: '6px' }}>
      <span style={{ ...detailLabelStyle, minWidth: '80px', marginBottom: 0 }}>요청항목</span>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
        {/* 윗줄: 항목A, 항목B, 항목C */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
          {['산학협력단 직인 날인', '외부공문발송', '과제신청'].map((item, idx) => (
            <label key={idx} style={radioLabelStyle}>
              <input 
                type="radio" 
                name="req_no" 
                value={idx + 1} 
                checked={(board.req_no || 1) === idx + 1}
                disabled // 상세 보기 모드이므로 수정 불가능하도록 비활성화 처리
                style={radioInputStyle}
              />
              {item}
            </label>
          ))}
        </div>

        {/* 아랫줄: 항목D, 항목E */}
        <div style={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
          {['협약진행', '과제입찰참가요청'].map((item, idx) => {
            const realValue = idx + 4;
            return (
              <label key={realValue} style={radioLabelStyle}>
                <input 
                  type="radio" 
                  name="req_no" 
                  value={realValue} 
                  checked={(board.req_no || 1) === realValue}
                  disabled
                  style={radioInputStyle}
                />
                {item}
              </label>
            );
          })}
        </div>
      </div>
    </div>
    
    {/* [행 1] 요청자 그룹 */}
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>요청자</span>
      <span style={detailValueStyle}>{board.fr_id || '-'}</span>
    </div>
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>연락처</span>
      <span style={detailValueStyle}>{board.tel || '-'}</span>
    </div>
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>이메일</span>
      <span style={detailValueStyle}>{board.email || '-'}</span>
    </div>

    {/* [행 2] 과제책임자 그룹 */}
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>과제책임자명</span>
      <span style={detailValueStyle}>{board.director || '-'}</span>
    </div>
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>과제책임자 학과명</span>
      <span style={detailValueStyle}>{board.major || '-'}</span>
    </div>
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>진행기한</span>
      <span style={detailValueStyle}>{board.finish_date || '-'}</span>
    </div>

    {/* [행 3] 과제지원기관 그룹 */}
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>과제지원기관</span>
      <span style={detailValueStyle}>{board.support_comp || '-'}</span>
    </div>
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>과제지원기관 담당자</span>
      <span style={detailValueStyle}>{board.support_name || '-'}</span>
    </div>
    <div style={detailBoxStyle}>
      <span style={detailLabelStyle}>과제지원기관 담당자연락처</span>
      <span style={detailValueStyle}>{board.support_tel || '-'}</span>
    </div>
  </div>


    {/* 3. 본문 상세 내용 및 하단 영역 (기존 스타일 기반 매칭) */}
    <p style={{ whiteSpace: 'pre-wrap', background: '#ffffff', padding: '16px 20px', border: '1px solid #E2E8F0', borderRadius: '8px', marginTop: '15px', lineHeight: '1.6', maxHeight: '200px', overflowY: 'auto', color: '#2D3748' }}>
      {board.contents}
    </p>

    <h4>첨부파일</h4>
    <div className="file-buttons">
      {selectedBoard?.files && selectedBoard.files.map(f => (
        <button
          key={f.seq}
          onClick={() => downloadFile(selectedBoard.idx, f.seq, f.file_nm)}
          style={styles.fileBtn}
        >
          📎 {f.file_nm}
        </button>
      ))}
      {(!selectedBoard?.files || selectedBoard.files.length === 0) && (
        <span style={{ fontSize: '13px', color: '#aaa' }}>첨부된 파일이 없습니다.</span>
      )}
    </div>

    <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
      {!board.reply_idx ? (
        <button onClick={onReplyClick} style={{ ...styles.btnPrimary, background: '#e67e22' }}>💬 Feedback 작성</button>
      ) : <div />}
      <button onClick={onBack} style={{ ...styles.btnPrimary, background: '#666' }}>닫기</button>
    </div>
  </div>
  );
}

// ==========================================
// 인라인 스타일 객체
// ==========================================
const styles = {
  th: { padding: '10px', fontSize: '14px' },
  td: { padding: '10px', fontSize: '14px' },
  input: { width: '100%', padding: '8px', boxSizing: 'border-box', border: '1px solid #ccc', borderRadius: '4px', marginBottom: '10px' },
  row: { display: 'flex', gap: '10px' },
  btnPrimary: { padding: '8px 16px', background: '#333', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' },
  replyBox: { background: '#f4f7f9', padding: '10px', borderRadius: '4px', borderLeft: '4px solid #0066cc' },
  
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
  },
  modalContent: {
    backgroundColor: '#fff', padding: '25px', borderRadius: '8px',
    width: '100%', maxWidth: '650px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
    position: 'relative', boxSizing: 'border-box'
  },
  modalCloseBtn: {
    position: 'absolute', top: '10px', right: '15px',
    background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#999'
  }
};
